import React, { useEffect, useState } from "react";
import { Plus, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { Field, Input, Textarea, Select } from "@/components/Field";

export default function Tickets() {
  const { t, lang, sl } = useI18n();
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [view, setView] = useState("list"); // list | new | detail
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState("");
  const [form, setForm] = useState({ subject: "", category: "general", priority: "normal", description: "" });

  const load = () => api.get("/tickets").then((r) => setTickets(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try { await api.post("/tickets", form); toast.success("OK"); setView("list"); setForm({ subject: "", category: "general", priority: "normal", description: "" }); load(); }
    catch (err) { toast.error(apiErr(err)); }
  };
  const open = async (id) => { const { data } = await api.get(`/tickets/${id}`); setActive(data); setView("detail"); };
  const sendReply = async (e) => {
    e.preventDefault();
    try { const { data } = await api.post(`/tickets/${active.id}/reply`, { body: reply }); setActive(data); setReply(""); }
    catch (err) { toast.error(apiErr(err)); }
  };

  if (view === "new") return (
    <div className="max-w-xl space-y-4">
      <button onClick={() => setView("list")} className="inline-flex items-center gap-2 text-sm link-muted"><ArrowLeft size={16} /> {t("common.back")}</button>
      <form onSubmit={create} className="surface p-6 space-y-4" data-testid="ticket-form">
        <h1 className="font-display text-xl font-black">{t("portal.newTicket")}</h1>
        <Field label={t("contact.subject")}><Input testid="ticket-subject" required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={lang === "de" ? "Kategorie" : "Category"}><Select testid="ticket-cat" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}><option value="general">{lang === "de" ? "Allgemein" : "General"}</option><option value="technical">{lang === "de" ? "Technisch" : "Technical"}</option><option value="billing">{lang === "de" ? "Abrechnung" : "Billing"}</option></Select></Field>
          <Field label={lang === "de" ? "Priorität" : "Priority"}><Select testid="ticket-prio" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}><option value="low">{sl("low")}</option><option value="normal">{sl("normal")}</option><option value="high">{sl("high")}</option><option value="urgent">{sl("urgent")}</option></Select></Field>
        </div>
        <Field label={t("contact.message")}><Textarea testid="ticket-desc" required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
        <button className="btn-primary w-full" data-testid="ticket-submit">{t("common.create")}</button>
      </form>
    </div>
  );

  if (view === "detail" && active) return (
    <div className="max-w-2xl space-y-4">
      <button onClick={() => setView("list")} className="inline-flex items-center gap-2 text-sm link-muted"><ArrowLeft size={16} /> {t("common.back")}</button>
      <div className="surface p-6">
        <div className="flex items-center justify-between mb-2"><h1 className="font-display text-xl font-black">{active.subject}</h1><span className="rounded-full bg-primary/15 text-primary px-3 py-1 text-xs font-semibold capitalize">{sl(active.status)}</span></div>
        <p className="text-sm text-muted-foreground">{active.description}</p>
        <div className="mt-6 space-y-3">
          {(active.replies || []).map((r) => (
            <div key={r.id} className={`rounded-lg p-3 text-sm ${r.role === "staff" ? "bg-primary/10" : "bg-secondary"}`}>
              <div className="text-xs font-semibold mb-1">{r.author} · {r.role}</div>{r.body}
            </div>
          ))}
        </div>
        <form onSubmit={sendReply} className="mt-4 flex gap-2">
          <Input testid="ticket-reply" value={reply} onChange={(e) => setReply(e.target.value)} placeholder={t("portal.reply")} />
          <button className="btn-primary !px-4" data-testid="ticket-reply-send">{t("common.send")}</button>
        </form>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl font-black">{t("portal.tickets")}</h1>
        <button onClick={() => setView("new")} className="btn-primary !py-2 !px-4 text-sm" data-testid="new-ticket"><Plus size={16} /> {t("portal.newTicket")}</button>
      </div>
      <div className="surface overflow-hidden">
        {tickets.length === 0 ? <p className="p-8 text-center text-muted-foreground">{t("common.none")}</p> : (
          <table className="w-full text-sm">
            <thead className="bg-secondary text-left text-muted-foreground"><tr><th className="p-4">Nr.</th><th className="p-4">{t("contact.subject")}</th><th className="p-4">Priorität</th><th className="p-4">{t("common.status")}</th></tr></thead>
            <tbody>
              {tickets.map((tk) => (
                <tr key={tk.id} onClick={() => open(tk.id)} className="border-t border-border cursor-pointer hover:bg-secondary" data-testid={`ticket-${tk.id}`}>
                  <td className="p-4 font-mono">{tk.number}</td><td className="p-4">{tk.subject}</td>
                  <td className="p-4 capitalize">{sl(tk.priority)}</td>
                  <td className="p-4"><span className="rounded-full bg-primary/15 text-primary px-2.5 py-0.5 text-xs font-semibold capitalize">{sl(tk.status)}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
